import React from 'react';
import {
  RangoBrazo,
  ModificadoresBrazo,
  RangoAntebrazo,
  ModificadorAntebrazo,
  RangoMuneca,
  ModificadorMuneca,
  RangoGiroMuneca,
  RangoCuello,
  ModificadoresCuello,
  RangoTronco,
  ModificadoresTronco,
  CondicionPiernas,
} from '../../lib/rula';

// ============================================================
// Diagramas de referencia RULA — silueta lateral 2D con volumen
// (torso/piernas/cabeza reutilizados literalmente entre secciones),
// segmento medido resaltado en verde, arco/etiqueta de ángulo con
// fondo propio (pill) para nunca perder contraste sobre la silueta.
//
// Geometría, ángulos y layout verificados en preview aislado antes
// de esta integración: checker de colisiones (bounding boxes) +
// captura real de navegador en cada sección — ver sesión de diseño.
// No ajustar coordenadas sin repetir esa verificación.
// ============================================================

export const TRUNK_AXIS = (
  <line x1={110} y1={25} x2={110} y2={280} stroke="#475569" strokeWidth={2.5} strokeDasharray="5 5" />
);

export const LEGS_NEUTRAL = (
  <>
    <rect x={95} y={192} width={19} height={73} rx={9} fill="#e2e8f0" opacity={0.35} />
    <ellipse cx={104.5} cy={268} rx={13} ry={6.5} fill="#e2e8f0" opacity={0.35} />
    <rect x={109} y={192} width={19} height={76} rx={9} fill="#e2e8f0" opacity={0.9} />
    <ellipse cx={118.5} cy={270} rx={13} ry={6.5} fill="#e2e8f0" opacity={0.9} />
  </>
);

export const TORSO_STATIC = (
  <path
    d="M 88,94 C 83,125 87,159 98,195 L 134,195 C 144,159 147,125 142,94 C 128,104 100,104 88,94 Z"
    fill="#e2e8f0"
    opacity={0.9}
  />
);
export const NECK_STATIC = <rect x={103} y={73} width={18} height={13} rx={4} fill="#e2e8f0" opacity={0.9} />;
export const HEAD_STATIC = <circle cx={110} cy={56} r={21} fill="#e2e8f0" opacity={0.9} />;

function Pill({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <rect x={x} y={y} width={w} height={h} rx={5} fill="#0b1220" opacity={0.9} stroke="#1e293b" strokeWidth={1} />;
}

const diagramSvgProps = { width: 260, height: 300, viewBox: '0 0 260 300' } as const;

// ===== Brazo =====

const BRAZO_ANGLE: Record<RangoBrazo, number> = {
  neutro: -15,
  extension20_o_flexion20a45: -32,
  flexion45a90: -70,
  flexionMayor90: -115,
};
const BRAZO_LABEL: Record<RangoBrazo, string> = {
  neutro: '0°–20°',
  extension20_o_flexion20a45: '20°–45°',
  flexion45a90: '45°–90°',
  flexionMayor90: '>90°',
};
// eje/arco de brazo por rango (hombro fijo en 110,98) -- verificados en preview
const BRAZO_ARC: Record<RangoBrazo, { line: [number, number]; arcD: string }> = {
  neutro: { line: [123.5, 148.1], arcD: 'M 110 137 A 39 39 0 0 0 120.1 135.5' },
  extension20_o_flexion20a45: { line: [137.6, 142.1], arcD: 'M 110 137 A 39 39 0 0 0 130.7 131.1' },
  flexion45a90: { line: [158.9, 115.8], arcD: 'M 110 137 A 39 39 0 0 0 146.6 111.3' },
  flexionMayor90: { line: [157.1, 76.0], arcD: 'M 110 137 A 39 39 0 0 0 145.3 81.5' },
};

export function RulaBrazoDiagram({ rango, mod }: { rango: RangoBrazo; mod: ModificadoresBrazo }) {
  const angle = BRAZO_ANGLE[rango];
  const { line, arcD } = BRAZO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      {rango === 'extension20_o_flexion20a45' && (
        <g transform="translate(110,98) scale(1.3) rotate(25)" opacity={0.4}>
          <rect x={-7} y={0} width={14} height={40} rx={7} fill="none" stroke="#94a3b8" strokeWidth={2.5} strokeDasharray="4 4" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(${angle})`}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#2FBF71" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" opacity={0.75} />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" opacity={0.75} />
        </g>
      </g>
      <line x1={110} y1={98} x2={line[0]} y2={line[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={16} fontWeight={700} textAnchor="middle">{BRAZO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje brazo</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">eje tronco</text>

      {mod.hombroElevadoORotado && (
        <path d="M 30,64 L 30,44 M 24,50 L 30,44 L 36,50" stroke="#3b82f6" strokeWidth={3.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {mod.abducido && (
        <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {mod.puntoApoyo && <rect x={18} y={126} width={24} height={8} rx={3} fill="#3b82f6" opacity={0.85} />}
    </svg>
  );
}

// ===== Cuello =====

const CUELLO_ANGLE: Record<RangoCuello, number> = {
  flexion0a10: 8,
  flexion10a20: 18,
  flexionMayor20: 32,
  extension: -18,
};
const CUELLO_LABEL: Record<RangoCuello, string> = {
  flexion0a10: '0°–10°',
  flexion10a20: '>10° y ≤20°',
  flexionMayor20: '>20°',
  extension: 'extensión',
};
const CUELLO_LABEL_SIZE: Record<RangoCuello, number> = {
  flexion0a10: 16,
  flexion10a20: 14,
  flexionMayor20: 16,
  extension: 16,
};
const CUELLO_ARC: Record<RangoCuello, { head: [number, number]; arcD: string }> = {
  flexion0a10: { head: [114.5, 56.3], arcD: 'M 110 52 A 36 36 0 0 1 115.0 52.4' },
  flexion10a20: { head: [119.9, 57.6], arcD: 'M 110 52 A 36 36 0 0 1 121.1 53.8' },
  flexionMayor20: { head: [127.0, 60.9], arcD: 'M 110 52 A 36 36 0 0 1 129.1 57.5' },
  extension: { head: [100.1, 57.6], arcD: 'M 110 52 A 36 36 0 0 0 98.9 53.8' },
};

export function RulaCuelloDiagram({ rango, mod }: { rango: RangoCuello; mod: ModificadoresCuello }) {
  const angle = CUELLO_ANGLE[rango];
  const { head, arcD } = CUELLO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-8)" opacity={0.55}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
        </g>
      </g>
      <g transform={`translate(110,88) rotate(${angle})`}>
        <rect x={-9} y={-15} width={18} height={13} rx={4} fill="#2FBF71" />
        <circle cx={0} cy={-32} r={21} fill="#2FBF71" />
      </g>
      <line x1={110} y1={88} x2={head[0]} y2={head[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={CUELLO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{CUELLO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje cuello</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">eje tronco</text>

      {mod.rotada && (
        <>
          <path d="M 30,53 a 9,9 0 1 1 -3,-6" stroke="#3b82f6" strokeWidth={3} fill="none" strokeLinecap="round" />
          <path d="M 25,45 l 5,2 -2,5 Z" fill="#3b82f6" />
        </>
      )}
      {mod.inclinacionLateral && (
        <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// ===== Tronco =====

const TRONCO_ANGLE: Record<RangoTronco, number> = {
  erguidoOApoyado: 5,
  flexion0a20: 12,
  flexion20a60: 40,
  flexionMayor60: 62,
};
const TRONCO_LABEL: Record<RangoTronco, string> = {
  erguidoOApoyado: 'erguido / apoyado',
  flexion0a20: '>0° y ≤20°',
  flexion20a60: '>20° y ≤60°',
  flexionMayor60: '>60°',
};
const TRONCO_LABEL_SIZE: Record<RangoTronco, number> = {
  erguidoOApoyado: 14,
  flexion0a20: 16,
  flexion20a60: 16,
  flexionMayor60: 16,
};
const TRONCO_ARC: Record<RangoTronco, { line: [number, number]; arcD: string }> = {
  erguidoOApoyado: { line: [118.7, 93.4], arcD: 'M 110 148 A 45 45 0 0 1 113.9 148.2' },
  flexion0a20: { line: [130.8, 95.2], arcD: 'M 110 148 A 45 45 0 0 1 119.4 149.0' },
  flexion20a60: { line: [174.3, 116.4], arcD: 'M 110 148 A 45 45 0 0 1 138.9 158.5' },
  flexionMayor60: { line: [198.3, 146.1], arcD: 'M 110 148 A 45 45 0 0 1 149.7 171.9' },
};

export function RulaTroncoDiagram({ rango, mod }: { rango: RangoTronco; mod: ModificadoresTronco }) {
  const angle = TRONCO_ANGLE[rango];
  const { line, arcD } = TRONCO_ARC[rango];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {/* Tronco es el segmento medido: cuello+cabeza+brazo (atenuados) rotan JUNTO
          con el torso desde la cadera (110,193) -- si solo rotara el torso y la
          cabeza quedara fija, se repetiria el bug de geometria ya corregido en Cuello. */}
      <g transform={`translate(110,193) rotate(${angle})`}>
        <path d="M -22,-99 C -27,-68 -23,-34 -12,2 L 24,2 C 34,-34 37,-68 32,-99 C 18,-89 -10,-89 -22,-99 Z" fill="#2FBF71" />
        <rect x={-7} y={-120} width={18} height={13} rx={4} fill="#e2e8f0" opacity={0.9} />
        <circle cx={0} cy={-137} r={21} fill="#e2e8f0" opacity={0.9} />
        <g transform="translate(0,-95) scale(1.3) rotate(-8)" opacity={0.55}>
          <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
          <g transform="translate(0,40) rotate(-25)">
            <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
            <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
          </g>
        </g>
      </g>
      <line x1={110} y1={193} x2={line[0]} y2={line[1]} stroke="#2FBF71" strokeWidth={2} strokeDasharray="3 3" opacity={0.9} />
      <path d={arcD} stroke="#f59e0b" strokeWidth={3} fill="none" />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={TRONCO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{TRONCO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje tronco</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">vertical</text>

      {mod.rotado && (
        <>
          <path d="M 30,53 a 9,9 0 1 1 -3,-6" stroke="#3b82f6" strokeWidth={3} fill="none" strokeLinecap="round" />
          <path d="M 25,45 l 5,2 -2,5 Z" fill="#3b82f6" />
        </>
      )}
      {mod.inclinacionLateral && (
        <path d="M 17,95 L 43,95 M 23,89 L 17,95 L 23,101 M 37,89 L 43,95 L 37,101" stroke="#f59e0b" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

// ===== Antebrazo =====
// El brazo superior queda fijo/atenuado (gris, no invisible contra el torso);
// el antebrazo es el segmento medido. "aLadoDelCuerpo"/"cruzaLineaMedia" son
// movimientos fuera del plano lateral -- se aproximan con rotacion adicional
// dentro del mismo plano 2D (simplificacion declarada y aprobada).

const ANTEBRAZO_BASE: Record<RangoAntebrazo, { angle: number; ghost: number | null }> = {
  flexion60a100: { angle: 80, ghost: null },
  fueraDe60a100: { angle: 110, ghost: 40 },
};
const ANTEBRAZO_MOD_OFFSET: Record<ModificadorAntebrazo, number> = {
  ninguno: 0,
  aLadoDelCuerpo: -15,
  cruzaLineaMedia: 20,
};
const ANTEBRAZO_LABEL: Record<RangoAntebrazo, string> = {
  flexion60a100: 'flexión 60°–100°',
  fueraDe60a100: 'flexión <60° o >100°',
};
const ANTEBRAZO_MOD_LABEL: Record<ModificadorAntebrazo, string> = {
  ninguno: 'ningún modificador',
  aLadoDelCuerpo: 'a un lado del cuerpo',
  cruzaLineaMedia: 'cruza línea media',
};

export function RulaAntebrazoDiagram({ rango, mod }: { rango: RangoAntebrazo; mod: ModificadorAntebrazo }) {
  const { angle: base, ghost } = ANTEBRAZO_BASE[rango];
  const nested = base + ANTEBRAZO_MOD_OFFSET[mod];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)">
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      </g>
      {ghost !== null && (
        <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(${ghost})`} opacity={0.4}>
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="none" stroke="#94a3b8" strokeWidth={2} strokeDasharray="3 3" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(${nested})`}>
        <rect x={-6} y={0} width={12} height={34} rx={6} fill="#2FBF71" />
        <circle cx={0} cy={36} r={7} fill="#2FBF71" />
      </g>

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={rango === 'fueraDe60a100' ? 14 : 16} fontWeight={700} textAnchor="middle">{ANTEBRAZO_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje antebrazo</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">{ANTEBRAZO_MOD_LABEL[mod]}</text>
    </svg>
  );
}

// ===== Muñeca =====
// Brazo superior + antebrazo quedan fijos/atenuados (misma referencia que
// Antebrazo); la mano es el segmento medido, rotando desde la muñeca.
// Angulos exagerados a proposito (no a escala biomecanica real) para que la
// diferencia entre variantes sea evidente a simple vista -- criterio pedido
// por el usuario.

const MUNECA_BASE: Record<RangoMuneca, { angle: number; ghost: number | null }> = {
  neutra: { angle: 0, ghost: null },
  flexionExtension0a15: { angle: 28, ghost: -28 },
  flexionExtensionMayor15: { angle: 50, ghost: -50 },
};
const MUNECA_MOD_OFFSET: Record<ModificadorMuneca, number> = {
  ninguno: 0,
  desviacionRadial: 30,
  desviacionCubital: -30,
};
const MUNECA_LABEL: Record<RangoMuneca, string> = {
  neutra: 'posición neutra',
  flexionExtension0a15: 'flexión/extensión >0° y <15°',
  flexionExtensionMayor15: 'flexión/extensión >15°',
};
const MUNECA_MOD_LABEL: Record<ModificadorMuneca, string> = {
  ninguno: 'ninguna',
  desviacionRadial: 'desviación radial',
  desviacionCubital: 'desviación cubital',
};

export function RulaMunecaDiagram({ rango, mod }: { rango: RangoMuneca; mod: ModificadorMuneca }) {
  const { angle: base, ghost } = MUNECA_BASE[rango];
  const nested = base + MUNECA_MOD_OFFSET[mod];
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)">
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      </g>
      <g transform="translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80)">
        <rect x={-6} y={0} width={12} height={34} rx={6} fill="#94a3b8" />
      </g>
      {ghost !== null && (
        <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80) translate(0,34) rotate(${ghost})`} opacity={0.4}>
          <rect x={-5} y={0} width={10} height={26} rx={4} fill="none" stroke="#94a3b8" strokeWidth={2} strokeDasharray="3 3" />
        </g>
      )}
      <g transform={`translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80) translate(0,34) rotate(${nested})`}>
        <rect x={-5} y={0} width={10} height={26} rx={4} fill="#2FBF71" />
        <circle cx={0} cy={26} r={5} fill="#2FBF71" />
      </g>

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={rango === 'neutra' ? 16 : 13} fontWeight={700} textAnchor="middle">{MUNECA_LABEL[rango]}</text>
      <Pill x={145} y={28} w={50} h={32} />
      <text x={170} y={50} fill="#f59e0b" fontSize={24} fontWeight={700} textAnchor="middle">θ</text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill="#2FBF71" fontSize={16} fontWeight={600} textAnchor="middle">eje muñeca</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill="#94a3b8" fontSize={12} fontWeight={600} textAnchor="middle">{MUNECA_MOD_LABEL[mod]}</text>
    </svg>
  );
}

// ===== Giro de muñeca =====
// La pronosupinacion gira sobre el propio eje longitudinal del antebrazo
// (entra/sale del plano de la pantalla) -- imposible de dibujar literalmente
// en esta silueta lateral 2D. Se representa con una flecha circular sobre la
// muñeca cuyo barrido indica la magnitud (mismo criterio de "exagerar para
// dar claridad" aprobado para Muñeca).

export function RulaGiroMunecaDiagram({ rango }: { rango: RangoGiroMuneca }) {
  const medio = rango === 'medio';
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {LEGS_NEUTRAL}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)">
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      </g>
      <g transform="translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80)">
        <rect x={-6} y={0} width={12} height={34} rx={6} fill="#94a3b8" />
      </g>
      <g transform="translate(110,98) scale(1.3) rotate(-35) translate(0,40) rotate(80) translate(0,34) rotate(0)">
        <rect x={-5} y={0} width={10} height={26} rx={4} fill="#94a3b8" />
        <circle cx={0} cy={26} r={5} fill="#94a3b8" />
      </g>

      {medio ? (
        <>
          <path d="M 92,158 A 22 22 0 1 1 88,185" stroke="#2FBF71" strokeWidth={3.5} fill="none" strokeLinecap="round" />
          <path d="M 83,180 l 5,7 8,-3 Z" fill="#2FBF71" />
        </>
      ) : (
        <>
          <path d="M 108.6,149.9 A 22 22 0 1 1 92,158" stroke="#f59e0b" strokeWidth={4.5} fill="none" strokeLinecap="round" />
          <path d="M 87,161 l 8,4 3,-8 Z" fill="#f59e0b" />
        </>
      )}

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={medio ? 15 : 16} fontWeight={700} textAnchor="middle">
        {medio ? 'pronación/supinación media' : 'pronación/supinación extrema'}
      </text>
      <Pill x={145} y={134} w={110} h={26} />
      <text x={200} y={152} fill={medio ? '#2FBF71' : '#f59e0b'} fontSize={16} fontWeight={600} textAnchor="middle">giro antebrazo</text>
    </svg>
  );
}

// ===== Piernas =====
// Las piernas mismas son el segmento medido (verde); el resto del cuerpo
// queda atenuado. Para "no apoyadas/asimetrico" la pierna trasera rota
// desde la cadera y queda visiblemente separada de la linea de piso.

export function RulaPiernasDiagram({ condicion }: { condicion: CondicionPiernas }) {
  const equilibrado = condicion === 'apoyadasYEquilibrado';
  return (
    <svg {...diagramSvgProps}>
      {TRUNK_AXIS}
      {TORSO_STATIC}
      {NECK_STATIC}
      {HEAD_STATIC}
      <g transform="translate(110,98) scale(1.3) rotate(-35)" opacity={0.55}>
        <rect x={-7} y={0} width={14} height={40} rx={7} fill="#e2e8f0" />
        <g transform="translate(0,40) rotate(-25)">
          <rect x={-6} y={0} width={12} height={34} rx={6} fill="#e2e8f0" />
          <circle cx={0} cy={36} r={7} fill="#e2e8f0" />
        </g>
      </g>

      {equilibrado ? (
        <>
          <rect x={95} y={192} width={19} height={73} rx={9} fill="#2FBF71" opacity={0.55} />
          <ellipse cx={104.5} cy={268} rx={13} ry={6.5} fill="#2FBF71" opacity={0.55} />
          <rect x={109} y={192} width={19} height={76} rx={9} fill="#2FBF71" />
          <ellipse cx={118.5} cy={270} rx={13} ry={6.5} fill="#2FBF71" />
        </>
      ) : (
        <>
          <g transform="translate(104.5,192) rotate(-32)">
            <rect x={-9.5} y={0} width={19} height={73} rx={9} fill="#2FBF71" opacity={0.55} />
            <ellipse cx={0} cy={76} rx={13} ry={6.5} fill="#2FBF71" opacity={0.55} />
          </g>
          <rect x={109} y={192} width={19} height={76} rx={9} fill="#2FBF71" />
          <ellipse cx={118.5} cy={270} rx={13} ry={6.5} fill="#2FBF71" />
        </>
      )}
      <line x1={60} y1={277} x2={165} y2={277} stroke="#94a3b8" strokeWidth={2} />

      <Pill x={55} y={4} w={150} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={equilibrado ? 13 : 12} fontWeight={700} textAnchor="middle">
        {equilibrado ? 'apoyadas y equilibrado' : 'no apoyadas / peso asimétrico'}
      </text>
    </svg>
  );
}
